import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import { useLang } from "@/i18n";
import { http, errText, SIGN_KEYS } from "@/lib/api";
import { BirthFields, emptyBirth, toPayload } from "@/components/tools/BirthFields";
import { NorthChart, SouthChart } from "@/components/tools/Charts";
import { FormError, PageShell } from "@/components/site/PageShell";

const Summary = ({ t, data }) => {
  const signs = SIGN_KEYS.map((k) => t.rashifal.signs[k]);
  const items = [
    [t.kundali.lagna, `${signs[data.lagna.sign]} ${data.lagna.degree.toFixed(2)}°`],
    [t.kundali.moonSign, signs[data.moon_sign]],
    [t.kundali.nakshatra, `${t.names.nakshatras[data.moon_nakshatra.index]} · ${t.kundali.pada} ${data.moon_nakshatra.pada}`],
    [t.kundali.current, data.current_dasha ? t.names.planets[data.current_dasha] : "-"],
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[color:var(--line)] border border-[color:var(--line)] rounded-3xl overflow-hidden">
      {items.map(([k, v], i) => (
        <div key={i} data-testid={`kundali-summary-${i}`} className="bg-white p-5"><p className="lbl">{k}</p><p className="display text-2xl">{v}</p></div>
      ))}
    </div>
  );
};

const PlanetTable = ({ t, data }) => (
  <div className="card overflow-x-auto">
    <table className="w-full text-sm" data-testid="planet-table">
      <thead><tr className="text-left text-[color:var(--muted)]">
        {[t.kundali.planet, t.kundali.sign, t.kundali.degree, t.kundali.house, t.kundali.nakshatra].map((h) => <th key={h} className="px-5 py-3 font-medium">{h}</th>)}
      </tr></thead>
      <tbody>
        {data.planets.map((p) => (
          <tr key={p.key} className="border-t border-[color:var(--line)]">
            <td className="px-5 py-3 font-semibold">{t.names.planets[p.key]}{p.retro && !["rahu", "ketu"].includes(p.key) && <span className="ml-1 text-[color:var(--saffron)]" title={t.kundali.retro}>*</span>}</td>
            <td className="px-5 py-3">{t.rashifal.signs[SIGN_KEYS[p.sign]]}</td>
            <td className="px-5 py-3">{p.degree.toFixed(2)}°</td>
            <td className="px-5 py-3">{p.house}</td>
            <td className="px-5 py-3">{t.names.nakshatras[p.nakshatra.index]} ({p.nakshatra.pada})</td>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="px-5 py-3 text-xs text-[color:var(--muted)]">* {t.kundali.retro} · {t.kundali.ayanamsa} {data.ayanamsa}°</p>
  </div>
);

const Dasha = ({ t, data }) => (
  <div className="card p-6">
    <h3 className="display text-2xl mb-4">{t.kundali.dasha}</h3>
    <ul className="divide-y divide-[color:var(--line)]" data-testid="dasha-list">
      {data.dasha.map((d) => (
        <li key={d.start} className={`flex justify-between gap-3 py-2.5 text-sm ${d.lord === data.current_dasha ? "text-[color:var(--saffron)] font-semibold" : ""}`}>
          <span>{t.names.planets[d.lord]}{d.lord === data.current_dasha ? ` · ${t.kundali.current}` : ""}</span>
          <span className="text-[color:var(--muted)]">{d.start} → {d.end}</span>
        </li>
      ))}
    </ul>
  </div>
);

const ChartResult = ({ t, data }) => {
  const [style, setStyle] = useState("north");
  const signs = SIGN_KEYS.map((k) => t.rashifal.signs[k]);
  return (
    <div className="space-y-8" data-testid="kundali-result">
      <Summary t={t} data={data} />
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="card p-6">
          <div className="flex gap-2 mb-6 no-print">
            {["north", "south"].map((s) => (
              <button key={s} data-testid={`chart-style-${s}`} onClick={() => setStyle(s)} className={`chip ${style === s ? "chip-on" : ""}`}>{t.kundali[s]}</button>
            ))}
          </div>
          {style === "north" ? <NorthChart t={t} data={data} /> : <SouthChart t={t} data={data} signs={signs} />}
        </div>
        <Dasha t={t} data={data} />
      </div>
      <PlanetTable t={t} data={data} />
      <div className="flex flex-wrap gap-3 no-print">
        <button data-testid="kundali-pdf-btn" onClick={() => window.print()} className="pill-ghost px-6 py-3.5 text-sm"><Download className="w-4 h-4 mr-2" />{t.kundali.pdf}</button>
        <Link to="/book" data-testid="kundali-book-btn" className="pill-solid px-7 py-3.5 text-sm">{t.common.bookCta}</Link>
      </div>
    </div>
  );
};

export default function KundaliPage() {
  const { t } = useLang();
  const [birth, setBirth] = useState(emptyBirth);
  const [state, setState] = useState({ loading: false, error: "", data: null });
  const submit = async (e) => {
    e.preventDefault();
    if (!birth.place) { setState({ loading: false, error: t.common.choosePlace, data: null }); return; }
    setState({ loading: true, error: "", data: null });
    try {
      const r = await http.post("/kundali", toPayload(birth));
      setState({ loading: false, error: "", data: r.data });
    } catch (err) {
      setState({ loading: false, error: errText(err, t.common.error), data: null });
    }
  };
  return (
    <PageShell eyebrow={t.kundali.eyebrow} title={t.kundali.title} desc={t.kundali.desc} testid="kundali">
      <form onSubmit={submit} className="card p-6 sm:p-8 max-w-2xl space-y-5 no-print" data-testid="kundali-form">
        <BirthFields t={t} value={birth} onChange={setBirth} prefix="kundali" />
        <FormError msg={state.error} />
        <button type="submit" data-testid="kundali-submit" disabled={state.loading} className="pill-solid px-7 py-3.5 text-sm disabled:opacity-60">
          {state.loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{state.loading ? t.common.calculating : t.common.calculate}
        </button>
      </form>
      {state.data && (
        <div className="mt-12">
          <div className="print-only mb-6 text-center">
            <img src="/logo.png" alt="Vedic Yagya Astrology" className="w-36 mx-auto" />
            <p className="mt-2 text-sm text-[color:var(--muted)]">Pandit Ajay Sagar Shukla · +91 94582 33787</p>
          </div>
          <p className="print-only display text-2xl mb-4">{[birth.name, birth.date, birth.time, birth.place?.name].filter(Boolean).join(" · ")}</p>
          <ChartResult t={t} data={state.data} />
        </div>
      )}
    </PageShell>
  );
}
